import { useEffect, useRef, useState } from 'react';
import Button from '../../../../components/forms/button.component';
import { useForm } from 'react-hook-form';
import useModal from '../../../../hooks/use-close-modal.hook';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/forms/icon.component';
import SimpleTitle from '../../../../components/forms/simple-titles.component';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { AlertService } from '../../../../services/alert.service';
import LogData from '../../../../components/logs/log-data.component';
import ReCAPTCHA from 'react-google-recaptcha';
import { ChannelsService } from '../../../../services/channels.service';
import { useDarkMode } from '../../../../hooks/use-dark-mode.hook';

interface SendMessageModalProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload?: any;
}

interface Operator {
  name: string;
  img: string;
  imgDark: string;
  width: string;
  height: string;
}

function SendMessageModalSMS({ show, onRequestClose, payload }: SendMessageModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const isDarkTheme = useDarkMode();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [operatorPhones, setOperatorPhones] = useState<Record<string, string>>({});
  const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({});

  const operators: Operator[] = [
    {
      name: 'telcel',
      img: '/assets/media/operators/telcel.png',
      imgDark: '/assets/media/operators/telcel-dark.png',
      width: '60px',
      height: '15px'
    },
    {
      name: 'movistar',
      img: '/assets/media/operators/movistar.png',
      imgDark: '/assets/media/operators/movistar-dark.png',
      width: '60em',
      height: '15px'
    },
    {
      name: 'att',
      img: '/assets/media/operators/att.png',
      imgDark: '/assets/media/operators/att-dark.png',
      width: '55em',
      height: '22px'
    },
    {
      name: 'other',
      img: '/assets/media/operators/altan.png',
      imgDark: '/assets/media/operators/altan-dark.png',
      width: '50em',
      height: '22px'
    }
  ];

  const activeOperators: Operator[] = operators.filter(
    (op) => Array.isArray(payload?.operators) && payload.operators.includes(op.name)
  );

  useModal(modalRef, show);

  const defaultValues = { output: payload?._id || '' };
  const {
    handleSubmit,
    reset,
    setValue,
    register,
    formState: { isSubmitting }
  } = useForm({ mode: 'onChange', defaultValues });

  const handleCaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const closeModal = () => {
    onRequestClose(false);
    reset({ output: payload?._id || '' });
    setOperatorPhones({});
    recaptchaRef.current?.reset();
    setPhoneErrors({});
  };

  const twelveDigitsRegex = /^\d{12}$/;
  const validatePhoneNumber = (phone: string) => twelveDigitsRegex.test(phone);

  const handleOperatorPhoneChange = (operatorName: string, newValue: string) => {
    const rawDigits = newValue.replace(/\D/g, '').slice(0, 12);

    setOperatorPhones((prev) => ({
      ...prev,
      [operatorName]: rawDigits
    }));

    if (rawDigits.length === 0 || (rawDigits.startsWith('52') && rawDigits.length <= 2)) {
      setPhoneErrors((prev) => {
        const copy = { ...prev };
        delete copy[operatorName];
        return copy;
      });
      return;
    }

    if (!twelveDigitsRegex.test(rawDigits)) {
      setPhoneErrors((prev) => ({
        ...prev,
        [operatorName]: 'Debe contener +52 seguido de 10 dígitos'
      }));
    } else {
      setPhoneErrors((prev) => {
        const copy = { ...prev };
        delete copy[operatorName];
        return copy;
      });
    }
  };

  const hasValidPhoneNumber = () => {
    return Object.values(operatorPhones).some((num) => validatePhoneNumber(num));
  };

  useEffect(() => {
    setValue('output', payload?._id || '');
  }, [payload, setValue]);
  register('output', { required: true });

  const onSubmit = async (data: { output: string }) => {
    if (!recaptchaToken) {
      AlertService.toastInfo('¡ Atención !, debes completar el reCAPTCHA.');
      return;
    }
    if (!hasValidPhoneNumber()) {
      AlertService.error(
        t('general.atention'),
        'Debe agregar al menos un número de teléfono válido'
      );
      return;
    }

    const operatorPhoneNumbers: { operator: string; phone_number: string }[] = [];
    Object.entries(operatorPhones).forEach(([operatorName, phone]) => {
      if (validatePhoneNumber(phone)) {
        operatorPhoneNumbers.push({
          operator: operatorName,
          phone_number: phone
        });
      }
    });

    const submitData: any = {
      output: data.output,
      operatorPhoneNumbers,
      recaptcha: recaptchaToken
    };

    const typeArray = Array.isArray(payload?.type) ? payload.type : [payload?.type].filter(Boolean);
    const messageCount = typeArray.length;
    const messageText =
      messageCount === 1
        ? t('channels.sms.send-message.a-message')
        : t('channels.sms.send-message.messages');

    const html = `
      <b>${t('channels.sms.send-message.question')}</b><br/>
      <p></p>
    `;
    const isConfirmed = await AlertService.question(t('general.atention'), html + messageText, () =>
      Promise.resolve(true)
    );
    if (!isConfirmed) return;

    try {
      const response = await ChannelsService.sendMessageTestSMS(submitData);
      if (Array.isArray(response.errors) && response.errors.length > 0) {
        const formattedErrors = response.errors
          .map((errorObj: Record<string, string>) => {
            const [key, value] = Object.entries(errorObj)[0];
            return `<div><strong>Número:</strong> ${value}</div>`;
          })
          .join('');

        const errorMessage =
          `<div class="mb-2">No se pueden enviar el mensaje de prueba. Uno o más números ingresados no existen o no pertenecen al operador seleccionado.</div><br/>` +
          formattedErrors;

        AlertService.error(t('general.atention'), errorMessage);
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } else {
        AlertService.toastSuccess(t('channels.sms.send-message.success'));
        LogData(submitData, 'sendSMSMessage', window.location.href);
        onRequestClose(true);
        reset({ output: payload?._id || '' });
        setOperatorPhones({});
        setPhoneErrors({});
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } catch (err: any) {
      console.error('Error:', err);
      AlertService.error(t('general.atention'), err.error || err);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  const anyInvalid = Object.keys(phoneErrors).length > 0;

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-1000px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={closeModal}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {t('channels.rcs.send-message-text')} <br />
                  <span className="link-primary fw-bold ms-2">{payload?.name}</span>
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span>Al menos debes agregar un número de un operador.</span>
                </div>
              </div>

              <div className="separator border-2 my-5 mb-15" />

              <div className="row mb-15">
                <div className="col-md-6 mb-5 d-flex justify-content-center">
                  <div className="bg-light-primary rounded rounded-12 p-4 mb-4 w-100">
                    <span className="text-dark fs-4 fw-bolder mb-3">Operadores Activos</span>
                    {activeOperators.map((operator) => {
                      const keyName = operator.name;
                      const val = operatorPhones[keyName] || '';
                      const errorMsg = phoneErrors[keyName];

                      return (
                        <div key={keyName} className="my-4">
                          <div className="mb-3 d-flex align-items-center gap-3">
                            <span className="theme-light-show">
                              <img
                                src={operator.img}
                                width={operator.width}
                                height={operator.height}
                              />
                            </span>
                            <span className="theme-dark-show">
                              <img
                                src={operator.imgDark}
                                width={operator.width}
                                height={operator.height}
                              />
                            </span>
                          </div>

                          <PhoneInput
                            country="mx"
                            onlyCountries={['mx']}
                            disableDropdown
                            countryCodeEditable={false}
                            value={val}
                            onChange={(value) => handleOperatorPhoneChange(keyName, value)}
                            inputClass={`phone-input-custom bg-body ${
                              errorMsg
                                ? 'is-invalid'
                                : val && twelveDigitsRegex.test(val)
                                  ? 'is-valid'
                                  : ''
                            }`}
                            containerClass="phone-input-container"
                            buttonClass="phone-input-button"
                            dropdownClass="phone-country-dropdown"
                            searchClass="search-box"
                            placeholder="55 1234 5678"
                          />

                          {errorMsg && <div className="invalid-feedback d-block">{errorMsg}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="col-md-6 mb-5">
                  <div className="text-center mb-10">
                    <h5 className="mb-3 text-danger fw-bolder fs-1">¡ Atención !</h5>
                    <div className="fs-5 fw-bolder text-start">
                      La entrega del mensaje puede tardar algunos minutos según el proveedor. Ahora
                      para enviar el mensaje de prueba, asegúrate de validar correctamente el
                      reCAPTCHA.
                    </div>
                  </div>
                  <img
                    src="/assets/media/illustrations/undraw/verify.svg"
                    alt="Verify"
                    className="ms-20"
                    width={'60%'}
                  />
                  <div className="d-flex justify-content-center mb-4">
                    <ReCAPTCHA
                      sitekey="6Lfec20rAAAAANyGkUlot_jUEWAbexPdSk8Nw_lu"
                      onChange={handleCaptchaChange}
                      theme={isDarkTheme ? 'dark' : 'light'}
                      ref={recaptchaRef}
                    />
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="d-flex flex-center flex-row-fluid">
                <button type="button" className="btn btn-light hover-scale" onClick={closeModal}>
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendSMSData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!hasValidPhoneNumber() || anyInvalid || isSubmitting || !recaptchaToken}
                  isLoading={isSubmitting}
                >
                  {t('general.send')}
                  <Icon icon="ki-send" size="fs-3" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SendMessageModalSMS;
